import React, { useState } from 'react';
import { ArrowLeftIcon, BuildingIcon, UserIcon, PhoneIcon, FileTextIcon, PaperclipIcon, DollarSignIcon, CalendarDaysIcon, InfoIcon, CheckIcon, SendIcon, UploadIcon, DownloadIcon, TrashIcon, GlobeIcon, ClockIcon, RepeatIcon, ScaleIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CONTRACTS, CONTRACT_TEMPLATES } from '../../data/windsor';
import { bookingStatusMeta, contractStatusMeta, findUsageType, formatTime, isAllDay, paymentMeta, paymentMethodLabel, resourcePath, usageTint } from './bookingHelpers';
import type { BookingFile, BookingSlot } from '../../types/loisirs';
interface Props {
  booking: BookingFile;
  onBack: () => void;
}
type Tab = 'general' | 'horaire' | 'contrat' | 'pieces' | 'frais';
export function BookingDetail({
  booking,
  onBack
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const fr = lang === 'fr';
  const locale = fr ? 'fr-CA' : 'en-CA';
  const [tab, setTab] = useState<Tab>('general');
  const [publicCalendar, setPublicCalendar] = useState(booking.publicCalendar);
  const [pricing, setPricing] = useState<'gratuit' | 'tarif'>(booking.billing.total === 0 ? 'gratuit' : 'tarif');
  const [slots, setSlots] = useState<BookingSlot[]>(booking.slots);
  const [scheduleView, setScheduleView] = useState<'sommaire' | 'detail'>('sommaire');
  const [note, setNote] = useState(booking.noteResponsable ?? '');
  const [invoiceNote, setInvoiceNote] = useState(booking.noteFacture ?? '');
  const [attachments, setAttachments] = useState(booking.attachments);
  const usage = findUsageType(booking.usageTypeId);
  const status = bookingStatusMeta(booking.status);
  const pay = paymentMeta(booking.payment.status);
  const contract = CONTRACTS.find(c => c.id === booking.contractId);
  const template = contract ? CONTRACT_TEMPLATES.find(tp => tp.id === contract.templateId) : undefined;
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  /** Groups occurrences by weekday + time to show the recurrence pattern. */
  const patterns = Array.from(slots.reduce((map, slot) => {
    const day = new Date(`${slot.date}T12:00:00`).toLocaleDateString(locale, {
      weekday: 'long'
    });
    const key = `${day}|${slot.startTime}|${slot.endTime}`;
    map.set(key, (map.get(key) ?? 0) + 1);
    return map;
  }, new Map<string, number>()).entries()).map(([key, count]) => {
    const [day, start, end] = key.split('|');
    return {
      day,
      start,
      end,
      count
    };
  });
  const tabs: {
    id: Tab;
    label: string;
    icon: typeof FileTextIcon;
    badge?: number;
  }[] = [{
    id: 'general',
    label: t('Général', 'General'),
    icon: InfoIcon
  }, {
    id: 'horaire',
    label: t('Horaire', 'Schedule'),
    icon: CalendarDaysIcon,
    badge: slots.length
  }, {
    id: 'contrat',
    label: t('Contrat', 'Contract'),
    icon: FileTextIcon
  }, {
    id: 'pieces',
    label: t('Pièces jointes', 'Attachments'),
    icon: PaperclipIcon,
    badge: attachments.length
  }, {
    id: 'frais',
    label: t('Frais', 'Fees'),
    icon: DollarSignIcon
  }];
  const fieldLabel = 'block text-xs font-medium text-slate-600 mb-1';
  const field = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]';

  /** Contract progress: generated → sent → signed. */
  const contractSteps = [{
    label: t('Généré', 'Generated'),
    done: Boolean(contract) && contract?.status !== 'a_generer'
  }, {
    label: t('Envoyé', 'Sent'),
    done: contract?.status === 'envoye' || contract?.status === 'signe' || contract?.status === 'en_retard'
  }, {
    label: t('Signé', 'Signed'),
    done: contract?.status === 'signe'
  }];
  return <div className="space-y-4 animate-in fade-in duration-200" data-id="element-2524" data-mp="238ws2gfc9o5z">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-2525" data-mp="2396i240dvvbo">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2526" data-mp="2vwk8dj9ktsmt" />
        {t('Retour au calendrier', 'Back to calendar')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2527" data-mp="2r0lnw6rdcuro">
        <div className="flex items-start justify-between gap-6 flex-wrap" data-id="element-2528" data-mp="2m6he7fqpy8mw">
          <div className="min-w-0" data-id="element-2529" data-mp="2h9hahbhcdhyy">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2530" data-mp="2h8fle33hquy8">
              <span className="text-xs font-bold text-slate-400 tabular-nums" data-id="element-2531" data-mp="3eocy94tcmxqb">
                {t('Réservation', 'Booking')} #{booking.number}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${status.className}`} data-id="element-2532" data-mp="3h8j45i1iwy9i">
                {status[lang]}
              </span>
              {usage && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border" style={usageTint(usage.color)} data-id="element-2533" data-mp="3r65xvanheudj">
                  <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: usage.color
              }} data-id="element-2534" data-mp="42iy914uvy95" />
                  {usage.label[lang]}
                </span>}
            </div>

            <h2 className="text-xl font-bold text-[#0F172A] font-heading mt-2 flex items-center gap-2" data-id="element-2535" data-mp="dtvs1ya7syht">
              {booking.client.kind === 'organisation' ? <BuildingIcon className="w-5 h-5 text-[#4F46E5] shrink-0" data-id="element-2536" data-mp="nr9wt0mrzyua" /> : <UserIcon className="w-5 h-5 text-[#4F46E5] shrink-0" data-id="element-2537" data-mp="rsln78dh4o3j" />}
              {booking.client.name}
            </h2>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500" data-id="element-2538" data-mp="2oq3n0he2zrzk">
              <span className="inline-flex items-center gap-1.5" data-id="element-2539" data-mp="2jraaar6mc50g">
                <BuildingIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2540" data-mp="2xm2y0cwnwri4" />
                {resourcePath(booking.resourceId, lang)}
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-2541" data-mp="37ifqj7gh0zba">
                <PhoneIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2542" data-mp="37guyppljjuh4" />
                {booking.client.phone}
              </span>
              <span data-id="element-2543" data-mp="3gjdujpqwt6tv">
                {t('Créée par ', 'Created by ')}
                <span className="font-medium text-slate-700" data-id="element-2544" data-mp="3gikvlay6zlma">
                  {booking.author}
                </span>
              </span>
            </div>
          </div>

          {/* Financial summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 shrink-0" data-id="element-2545" data-mp="1m7fq013joz4b">
            {[{
            label: t('Total', 'Total'),
            value: booking.billing.total
          }, {
            label: t('Facturé', 'Invoiced'),
            value: booking.billing.invoiced
          }, {
            label: t('Payé', 'Paid'),
            value: booking.billing.paid
          }, {
            label: t('Solde', 'Balance'),
            value: booking.billing.balance
          }].map((item, i) => <div key={item.label} className={`rounded-lg border px-3 py-2 min-w-[86px] ${i === 3 && item.value > 0 ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-slate-50'}`} data-id="element-2546" data-mp="138kydf4qupm5" data-mpi={i}>
                <p className="text-[11px] text-slate-500" data-id="element-2547" data-mp="fc73t1sxp4bx">{item.label}</p>
                <p className={`text-sm font-bold font-heading tabular-nums ${i === 3 && item.value > 0 ? 'text-amber-700' : 'text-[#0F172A]'}`} data-id="element-2548" data-mp="k9ywhy1ekuh6">
                  {money(item.value)}
                </p>
              </div>)}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap" data-id="element-2549" data-mp="3i9lxgetwrf1r">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${pay.className}`} data-id="element-2550" data-mp="3divsv3tb3g84">
            {pay[lang]}
          </span>
          <span className="text-xs text-slate-500" data-id="element-2551" data-mp="34i7e26w39bu6">
            {paymentMethodLabel(booking.payment.method)[lang]}
            {booking.payment.reference && ` · ${booking.payment.reference}`}
            {booking.payment.paidOn && ` · ${longDate(booking.payment.paidOn)}`}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-2552" data-mp="2qjr8wkdvjte5">
        <nav aria-label={t('Sections du dossier', 'File sections')} className="flex items-center gap-1 overflow-x-auto" data-id="element-2553" data-mp="sso9l6vse9wf">
          {tabs.map(item => {
          const Icon = item.icon;
          const active = tab === item.id;
          return <button key={item.id} onClick={() => setTab(item.id)} className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-id="element-2554" data-mp="26mlhyx8rb0fj">
                <Icon className="w-4 h-4" data-id="element-2555" data-mp="1so55epo7qfph" />
                {item.label}
                {item.badge !== undefined && item.badge > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${active ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-100 text-slate-500'}`} data-id="element-2556" data-mp="1wp77de9il5v5">
                    {item.badge}
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {/* --------------------------------------------------------- GENERAL */}
      {tab === 'general' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2557" data-mp="3bki6avaqi6vl">
          <div className="lg:col-span-2 space-y-4" data-id="element-2558" data-mp="3gjuzryw42c75">
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2559" data-mp="3gijole0c6260">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2560" data-mp="2ix4f7dtpw5je">
                {t('Dates', 'Dates')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2561" data-mp="2nx0hn8mrnowu">
                <div data-id="element-2562" data-mp="2sspc4xjxifma">
                  <label htmlFor="bk-start" className={fieldLabel} data-id="element-2563" data-mp="2swmwfmi8td7r">
                    {t('Date de début', 'Start date')}
                  </label>
                  <input id="bk-start" type="date" defaultValue={booking.startDate} className={field} data-id="element-2564" data-mp="82zw4h7hons0" />
                </div>
                <div data-id="element-2565" data-mp="1wn5sabka2fnb">
                  <label htmlFor="bk-end" className={fieldLabel} data-id="element-2566" data-mp="1wps0mdezsz6l">
                    {t('Date de fin', 'End date')}
                  </label>
                  <input id="bk-end" type="date" defaultValue={booking.endDate} className={field} data-id="element-2567" data-mp="1nukaoysduf02" />
                </div>
                <div data-id="element-2568" data-mp="1dxz4wb8jgzv6">
                  <label htmlFor="bk-billing" className={fieldLabel} data-id="element-2569" data-mp="1dyha3xd6iof8">
                    {t('Date de facturation', 'Billing date')}
                  </label>
                  <input id="bk-billing" type="date" defaultValue={booking.billingDate ?? ''} className={field} data-id="element-2570" data-mp="14vpwps0qtiky" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-id="element-2571" data-mp="2z869lbo1nuys">
                <div data-id="element-2572" data-mp="2ubz0xb49k5d5">
                  <label htmlFor="bk-attention" className={fieldLabel} data-id="element-2573" data-mp="2u9l7t7t59388">
                    {t('À l’attention de', 'Attention of')}
                  </label>
                  <input id="bk-attention" type="text" defaultValue={booking.attentionOf ?? ''} placeholder={t('Personne responsable', 'Responsible person')} className={field} data-id="element-2574" data-mp="3i7kkbho8eywk" />
                </div>
                <div data-id="element-2575" data-mp="z5pq017s4x0">
                  <label htmlFor="bk-usage" className={fieldLabel} data-id="element-2576" data-mp="wt85nzdbwkh">
                    {t('Type d’utilisation', 'Usage type')}
                  </label>
                  <input id="bk-usage" type="text" readOnly value={usage?.label[lang] ?? ''} className={`${field} bg-slate-50 text-slate-600`} data-id="element-2577" data-mp="3n4suow351yn9" />
                </div>
              </div>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2578" data-mp="2gr395922dmgr">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2579" data-mp="294xzhku8mn03">
                {t('Tarification', 'Pricing')}
              </h3>
              <div className="flex gap-2" data-id="element-2580" data-mp="2i6p930rwfdz8">
                {[{
              id: 'gratuit' as const,
              label: t('Gratuit', 'Free')
            }, {
              id: 'tarif' as const,
              label: t('Tarifé', 'Charged')
            }].map(option => <button key={option.id} onClick={() => setPricing(option.id)} className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${pricing === option.id ? 'border-[#4F46E5] bg-indigo-50/60 text-[#4F46E5] ring-1 ring-[#4F46E5]/20' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-2581" data-mp="1lgydszbs9msd">
                    {option.label}
                  </button>)}
              </div>
              {pricing === 'gratuit' && <p className="flex items-start gap-2 text-xs text-slate-500 mt-3" data-id="element-2582" data-mp="1kfapji843zl9">
                  <ScaleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" data-id="element-2583" data-mp="1pckllgq0b9i2" />
                  {t('Un contrat sera tout de même produit, à 0 $ — requis pour les ententes scolaires et les organismes reconnus.', 'A contract is still issued, at $0 — required for school agreements and recognized organizations.')}
                </p>}
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-id="element-2584" data-mp="2820mw69in5zj">
              <div data-id="element-2585" data-mp="2d2evbpf14hy3">
                <label htmlFor="bk-note" className={fieldLabel} data-id="element-2586" data-mp="2d0tzlwgzasyn">
                  {t('Note pour le responsable', 'Note for the manager')}
                </label>
                <textarea id="bk-note" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={t('Consignes internes, clés, accès…', 'Internal instructions, keys, access…')} className={field} data-id="element-2587" data-mp="ipp1t8skpar8" />
              </div>
              <div data-id="element-2588" data-mp="2i5xk1gpituo9">
                <label htmlFor="bk-invoice-note" className={fieldLabel} data-id="element-2589" data-mp="3eyatw1qy31mu">
                  {t('Notes imprimées sur la facture', 'Notes printed on the invoice')}
                </label>
                <textarea id="bk-invoice-note" rows={2} value={invoiceNote} onChange={e => setInvoiceNote(e.target.value)} className={field} data-id="element-2590" data-mp="35wbcv9fxjxjw" />
              </div>
            </section>
          </div>

          <div className="space-y-4" data-id="element-2591" data-mp="1phrxcoewpvls">
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2592" data-mp="1r0ii8p6fexhm">
              <div className="flex items-start justify-between gap-3" data-id="element-2593" data-mp="1qyfgj7vwvfx2">
                <div data-id="element-2594" data-mp="1vybplljgtxjh">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800" data-id="element-2595" data-mp="1vxiob03a008q">
                    <GlobeIcon className="w-4 h-4 text-slate-400" data-id="element-2596" data-mp="1hzk8rrd7xxrn" />
                    {t('Calendriers publics', 'Public calendars')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-2597" data-mp="189scmsph0wl2">
                    {publicCalendar ? t('Visible sur les calendriers publics et les réservations en ligne.', 'Visible on public calendars and online bookings.') : t('Masquée du public — usage interne seulement.', 'Hidden from the public — internal use only.')}
                  </p>
                </div>
                <button type="button" role="switch" aria-checked={publicCalendar} aria-label={t('Afficher sur les calendriers publics', 'Show on public calendars')} onClick={() => setPublicCalendar(v => !v)} className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${publicCalendar ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-2598" data-mp="aohgppandgh3">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${publicCalendar ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-2599" data-mp="13f7s04wjtyzd" />
                </button>
              </div>
            </section>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4" data-id="element-2600" data-mp="2eqe5s3owsrzj">
              <p className="flex items-start gap-2 text-sm text-[#3730A3]" data-id="element-2601" data-mp="2jp7lm2ro4v2v">
                <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-2602" data-mp="2ooty29zoa7am" />
                {t('Dès que le contrat est signé, les plages sont automatiquement publiées au calendrier.', 'As soon as the contract is signed, the slots are automatically published to the calendar.')}
              </p>
            </div>
          </div>
        </div>}

      {/* --------------------------------------------------------- SCHEDULE */}
      {tab === 'horaire' && <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2603" data-mp="29sclxgjnson0">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-id="element-2604" data-mp="1daq6fagk4v8q">
            <div data-id="element-2605" data-mp="1haqi4nc9usc3">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2606" data-mp="1ha8pe0otiw8x">
                {t('Plages générées', 'Generated slots')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2607" data-mp="14t67v8vg7tgy">
                {slots.length}{' '}
                {t('occurrences du ', 'occurrences from ')}
                {longDate(booking.startDate)}
                {t(' au ', ' to ')}
                {longDate(booking.endDate)}
              </p>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-2608" data-mp="qtyb0mmrcxfx">
              {[{
            id: 'sommaire' as const,
            label: t('Sommaire', 'Summary')
          }, {
            id: 'detail' as const,
            label: t('Détail', 'Detail')
          }].map(option => <button key={option.id} onClick={() => setScheduleView(option.id)} className={`px-3.5 py-1.5 text-sm font-medium transition-colors ${scheduleView === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-2609" data-mp="d3l32k9v9iql">
                  {option.label}
                </button>)}
            </div>
          </div>

          {scheduleView === 'sommaire' ? <ul className="divide-y divide-slate-100" data-id="element-2610" data-mp="3j449rva2g3b3">
              {patterns.map(pattern => <li key={`${pattern.day}-${pattern.start}`} className="px-6 py-4 flex items-center gap-3" data-id="element-2611" data-mp="3f2iww9xj7p0w">
                  <RepeatIcon className="w-4 h-4 text-[#4F46E5] shrink-0" data-id="element-2612" data-mp="3a9g3q2wo2hl8" />
                  <div className="min-w-0" data-id="element-2613" data-mp="35cpsbsvt4a65">
                    <p className="text-sm font-medium text-slate-800 capitalize" data-id="element-2614" data-mp="30dd53710x131">
                      {pattern.day}
                      {' · '}
                      {isAllDay(pattern.start, pattern.end) ? t('toute la journée', 'all day') : `${formatTime(pattern.start, fr)} – ${formatTime(pattern.end, fr)}`}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2615" data-mp="1avze5m85yqpo">
                      {pattern.count}{' '}
                      {t('occurrences', 'occurrences')}
                    </p>
                  </div>
                </li>)}
            </ul> : <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto" data-id="element-2616" data-mp="1ktdttjm9jbgx">
              {slots.map(slot => <li key={slot.id} className="group px-6 py-3 flex items-center gap-3 hover:bg-slate-50/60" data-id="element-2617" data-mp="n4k8pkl3bta7">
                  <ClockIcon className="w-4 h-4 text-slate-300 shrink-0" data-id="element-2618" data-mp="x3t1490b4zug" />
                  <div className="min-w-0 flex-1" data-id="element-2619" data-mp="11vkq4f3texih">
                    <p className="text-sm text-slate-800" data-id="element-2620" data-mp="11xdwruefhhqh">
                      {longDate(slot.date)}
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums" data-id="element-2621" data-mp="9eqk93dhzb10">
                      {isAllDay(slot.startTime, slot.endTime) ? t('Toute la journée', 'All day') : `${formatTime(slot.startTime, fr)} – ${formatTime(slot.endTime, fr)}`}
                    </p>
                  </div>
                  <button onClick={() => setSlots(prev => prev.filter(s => s.id !== slot.id))} aria-label={t('Retirer cette occurrence', 'Remove this occurrence')} className="p-2 rounded-lg text-slate-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-600 hover:bg-red-50 transition-all" data-id="element-2622" data-mp="b2spau6uupcc">
                    <TrashIcon className="w-4 h-4" data-id="element-2623" data-mp="3ognnbpiyrs2r" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {/* --------------------------------------------------------- CONTRACT */}
      {tab === 'contrat' && <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-2624" data-mp="ytgaui9p1dhd">
          <div className="lg:col-span-2 space-y-4" data-id="element-2625" data-mp="2gr1pmnzk6tk5">
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2626" data-mp="2gto0awhqxp6l">
              {contract ? <>
                  <div className="flex items-start justify-between gap-3 mb-4" data-id="element-2627" data-mp="27r6a66yz0ceb">
                    <div data-id="element-2628" data-mp="2cppw47dkdo6o">
                      <p className="text-xs font-bold text-slate-400" data-id="element-2629" data-mp="2coy5laytqgn9">
                        {contract.number}
                      </p>
                      <h3 className="text-base font-semibold text-[#0F172A] mt-0.5" data-id="element-2630" data-mp="30g3ro7vcy560">
                        {contract.title}
                      </h3>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${contractStatusMeta(contract.status).className}`} data-id="element-2631" data-mp="2qopp1r840p8n">
                      {contractStatusMeta(contract.status)[lang]}
                    </span>
                  </div>

                  {/* Timeline */}
                  <ol className="flex items-center gap-2 mb-5" data-id="element-2632" data-mp="1zjv5qkj475zz">
                    {contractSteps.map((step, i) => <li key={step.label} className="flex items-center gap-2 flex-1" data-id="element-2633" data-mp="1uinw0fxevwqo" data-mpi={i}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${step.done ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-2634" data-mp="1qixaorqnm8tj">
                          {step.done ? <CheckIcon className="w-3.5 h-3.5" data-id="element-2635" data-mp="29g7zfientjuq" /> : i + 1}
                        </span>
                        <span className={`text-xs font-medium ${step.done ? 'text-slate-800' : 'text-slate-400'}`} data-id="element-2636" data-mp="24hdh0hkltxb5">
                          {step.label}
                        </span>
                        {i < contractSteps.length - 1 && <span className={`flex-1 h-0.5 rounded ${contractSteps[i + 1].done ? 'bg-[#4F46E5]' : 'bg-slate-100'}`} data-id="element-2637" data-mp="39h1z481pyazo" />}
                      </li>)}
                  </ol>

                  <dl className="space-y-2 text-sm" data-id="element-2638" data-mp="3sfnz1jli1w20">
                    <div className="flex justify-between gap-3" data-id="element-2639" data-mp="3secwto7pcwd0">
                      <dt className="text-slate-500" data-id="element-2640" data-mp="1104xz3fxq6d">
                        {t('Modèle', 'Template')}
                      </dt>
                      <dd className="font-medium text-slate-800 text-right" data-id="element-2641" data-mp="5ztkry679t9p">
                        {template?.name[lang] ?? '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3" data-id="element-2642" data-mp="f1t0mn5harb2">
                      <dt className="text-slate-500" data-id="element-2643" data-mp="2b3wd3n8gc0y3">
                        {t('Rédigé par', 'Drafted by')}
                      </dt>
                      <dd className="font-medium text-slate-800" data-id="element-2644" data-mp="273vnd6fwk3bq">
                        {contract.source === 'greffe' ? t('Greffe', 'Clerk’s office') : t('Loisirs', 'Recreation')}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3" data-id="element-2645" data-mp="2ur3uzv0szogx">
                      <dt className="text-slate-500" data-id="element-2646" data-mp="2pt2972bztb1r">
                        {t('Montant', 'Amount')}
                      </dt>
                      <dd className="font-medium text-slate-800" data-id="element-2647" data-mp="33rs90pq75ott">
                        {contract.free ? t('0 $ — entente sans frais', '$0 — no-fee agreement') : money(booking.billing.total)}
                      </dd>
                    </div>
                    {contract.signedBy && <div className="flex justify-between gap-3" data-id="element-2648" data-mp="14htwk6arq9kn">
                        <dt className="text-slate-500" data-id="element-2649" data-mp="3g4dslo2mxrs3">
                          {t('Signé par', 'Signed by')}
                        </dt>
                        <dd className="font-medium text-slate-800 text-right" data-id="element-2650" data-mp="3l406lzqafc1y">
                          {contract.signedBy}
                          <span className="block text-xs text-slate-500" data-id="element-2651" data-mp="3bbufstbrv0oc">
                            {contract.signedOn && longDate(contract.signedOn)}
                          </span>
                        </dd>
                      </div>}
                  </dl>

                  {contract.status === 'en_retard' && <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-id="element-2652" data-mp="2odhi2u41rz7z">
                      {t('Envoyé il y a plus de 14 jours sans signature. Une relance est recommandée.', 'Sent more than 14 days ago without a signature. A reminder is recommended.')}
                    </p>}

                  <div className="flex flex-col gap-2 mt-5" data-id="element-2653" data-mp="3ak8stcssn8ng">
                    {contract.status === 'a_generer' ? <button className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2654" data-mp="30sct5xb27ah4">
                        <FileTextIcon className="w-4 h-4" data-id="element-2655" data-mp="2rrogb5xejg5t" />
                        {t('Générer le contrat', 'Generate contract')}
                      </button> : contract.status !== 'signe' ? <button className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2656" data-mp="2ht8r58hxqgyy">
                        <SendIcon className="w-4 h-4" data-id="element-2657" data-mp="g1vluic2272d" />
                        {t('Envoyer pour signature', 'Send for signature')}
                      </button> : <button className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2658" data-mp="jk1frq6febqq">
                        <DownloadIcon className="w-4 h-4" data-id="element-2659" data-mp="scmsopndlo7n" />
                        {t('Télécharger le contrat signé', 'Download signed contract')}
                      </button>}
                    <button className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2660" data-mp="lgu4dnazykh">
                      <UploadIcon className="w-4 h-4" data-id="element-2661" data-mp="9llyeex7aguf" />
                      {t('Téléverser le contrat signé', 'Upload signed contract')}
                    </button>
                    <button className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2662" data-mp="1l10uzhlhz6p0">
                      <ScaleIcon className="w-4 h-4" data-id="element-2663" data-mp="3rgahse6g6dmv" />
                      {t('Importer un contrat du greffe', 'Import a clerk’s office contract')}
                    </button>
                  </div>
                </> : <div className="text-center py-8" data-id="element-2664" data-mp="3hpfur7ldna0d">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3" data-id="element-2665" data-mp="3mpbwt1aikrda">
                    <FileTextIcon className="w-6 h-6 text-slate-400" data-id="element-2666" data-mp="37rjiudkm8b17" />
                  </div>
                  <p className="text-sm font-semibold text-slate-700" data-id="element-2667" data-mp="3crnpzczmvter">
                    {t('Aucun contrat rattaché', 'No contract attached')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 mb-4" data-id="element-2668" data-mp="2ytfuzam5r268">
                    {t('Le modèle « Location de glace » est présélectionné selon le type d’utilisation.', 'The “Ice Rental” template is preselected based on the usage type.')}
                  </p>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2669" data-mp="1c2i3nt4w15r2">
                    <FileTextIcon className="w-4 h-4" data-id="element-2670" data-mp="1723ug7rjvprg" />
                    {t('Générer le contrat', 'Generate contract')}
                  </button>
                </div>}
            </section>
          </div>

          {/* Document preview */}
          <div className="lg:col-span-3" data-id="element-2671" data-mp="1us7w49l6hfft">
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-id="element-2672" data-mp="1uvc6jop8l7ex">
              <div className="max-w-xl mx-auto" data-id="element-2673" data-mp="1pu6bhk83t828">
                <div className="text-center pb-5 border-b-2 border-[#0F172A]" data-id="element-2674" data-mp="23uh710kczatq">
                  <p className="text-sm font-bold text-[#0F172A] font-heading tracking-wide uppercase" data-id="element-2675" data-mp="1zsvlyrxo7q8g">
                    {t('Ville de Windsor', 'City of Windsor')}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-2676" data-mp="5ekxikkzmy37">
                    {t('Service des loisirs', 'Recreation Department')} · 11 rue
                    St-Georges
                  </p>
                </div>

                <div className="flex justify-between text-xs text-slate-500 mt-4 tabular-nums" data-id="element-2677" data-mp="29hw8oulehmqr">
                  <span data-id="element-2678" data-mp="2egzg2lous51w">
                    {t('N° client', 'Client no.')} {booking.client.id.toUpperCase()}
                  </span>
                  <span data-id="element-2679" data-mp="2jhdct7l07skm">
                    {t('N° réservation', 'Booking no.')} {booking.number}
                  </span>
                </div>

                <h4 className="text-center text-base font-bold text-[#0F172A] font-heading mt-5" data-id="element-2680" data-mp="2sha8ht5t9rp6">
                  {contract?.title ?? t('Contrat de location', 'Rental agreement')}
                </h4>

                <dl className="mt-5 space-y-2 text-sm" data-id="element-2681" data-mp="3273g6j2u1mo5">
                  <div className="flex gap-3" data-id="element-2682" data-mp="326k2jek7tkfx">
                    <dt className="w-24 shrink-0 text-slate-500" data-id="element-2683" data-mp="376g65cp039uy">
                      {t('Locateur', 'Lessor')}
                    </dt>
                    <dd className="text-slate-800" data-id="element-2684" data-mp="3b8ky86zsvyfh">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </dd>
                  </div>
                  <div className="flex gap-3" data-id="element-2685" data-mp="vibavrzvjkjb">
                    <dt className="w-24 shrink-0 text-slate-500" data-id="element-2686" data-mp="vhjdqcqmpeqx">
                      {t('Locataire', 'Lessee')}
                    </dt>
                    <dd className="text-slate-800" data-id="element-2687" data-mp="lkwtnq64mrpy">{booking.client.name}</dd>
                  </div>
                  <div className="flex gap-3" data-id="element-2688" data-mp="1j85my7cdd52i">
                    <dt className="w-24 shrink-0 text-slate-500" data-id="element-2689" data-mp="1j7vu8bzxwjt4">
                      {t('Plateau', 'Facility')}
                    </dt>
                    <dd className="text-slate-800" data-id="element-2690" data-mp="19im3azgu176l">
                      {resourcePath(booking.resourceId, lang)}
                    </dd>
                  </div>
                  <div className="flex gap-3" data-id="element-2691" data-mp="14izmyh5pjatk">
                    <dt className="w-24 shrink-0 text-slate-500" data-id="element-2692" data-mp="21yn1n80da04v">
                      {t('Période', 'Period')}
                    </dt>
                    <dd className="text-slate-800" data-id="element-2693" data-mp="1x7erg9r7qaqn">
                      {longDate(booking.startDate)} — {longDate(booking.endDate)}
                    </dd>
                  </div>
                  <div className="flex gap-3" data-id="element-2694" data-mp="1ls0krez09q8b">
                    <dt className="w-24 shrink-0 text-slate-500" data-id="element-2695" data-mp="1lpnvshutyflr">
                      {t('Montant', 'Amount')}
                    </dt>
                    <dd className="text-slate-800 font-medium" data-id="element-2696" data-mp="1cqkcbcrr3y6q">
                      {booking.billing.total === 0 ? t('0 $ — location sans frais', '$0 — no-fee rental') : money(booking.billing.total)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-2697" data-mp="1vp6d0qjcvna4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2698" data-mp="2jfit4oor8qbs">
                    {t('Clauses', 'Clauses')}
                  </p>
                  <ol className="space-y-1.5 text-xs text-slate-600 list-decimal list-inside" data-id="element-2699" data-mp="2o7twk1ipeu93">
                    <li data-id="element-2700" data-mp="cppafmhb5pm2">
                      {t('Le locataire s’engage à respecter les heures réservées et à libérer le plateau à l’heure convenue.', 'The lessee agrees to respect the reserved hours and to vacate the facility at the agreed time.')}
                    </li>
                    <li data-id="element-2701" data-mp="3pxij37lgwpp5">
                      {t('Une preuve d’assurance responsabilité civile de 2 000 000 $ est exigée avant la première utilisation.', 'Proof of $2,000,000 civil liability insurance is required before first use.')}
                    </li>
                    <li data-id="element-2702" data-mp="3tycboe9uusod">
                      {t('Toute annulation doit être transmise au moins 72 heures à l’avance, à défaut de quoi la plage est facturée.', 'Any cancellation must be submitted at least 72 hours in advance, failing which the slot is billed.')}
                    </li>
                    <li data-id="element-2703" data-mp="bul2rml1hlg0">
                      {t('Les dommages causés au plateau ou aux équipements sont facturés au locataire.', 'Damage to the facility or equipment is billed to the lessee.')}
                    </li>
                  </ol>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-slate-100" data-id="element-2704" data-mp="3kby13wgodg0y">
                  {[t('Pour la Ville de Windsor', 'For the City of Windsor'), t('Pour le locataire', 'For the lessee')].map(label => <div key={label} data-id="element-2705" data-mp="1xp2g45ao5mj">
                      <div className="h-8 border-b border-slate-300" data-id="element-2706" data-mp="1yqhejpflc9j" />
                      <p className="text-[11px] text-slate-500 mt-1" data-id="element-2707" data-mp="30lvyrx5b87op">{label}</p>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* ------------------------------------------------------ ATTACHMENTS */}
      {tab === 'pieces' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2708" data-mp="2t120oux67k9p">
          <label htmlFor="bk-upload" className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-6 py-10 cursor-pointer hover:border-[#4F46E5] hover:bg-indigo-50/30 transition-colors" data-id="element-2709" data-mp="2o2gyipycyqim">
            <UploadIcon className="w-6 h-6 text-slate-400" data-id="element-2710" data-mp="2abltxpuw6vku" />
            <span className="text-sm font-medium text-slate-700" data-id="element-2711" data-mp="25dk32ir85ryt">
              {t('Déposez un document ou cliquez pour parcourir', 'Drop a document or click to browse')}
            </span>
            <span className="text-xs text-slate-500" data-id="element-2712" data-mp="1wbuavapiqozz">
              {t('PDF, JPG ou XLSX — 10 Mo maximum', 'PDF, JPG or XLSX — 10 MB max')}
            </span>
            <input id="bk-upload" type="file" className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          if (file) {
            setAttachments(prev => [...prev, {
              name: file.name,
              size: `${Math.max(Math.round(file.size / 1024), 1)} Ko`
            }]);
          }
        }} data-id="element-2713" data-mp="3fah28yl1engo" />
          </label>

          {attachments.length === 0 ? <p className="text-center text-sm text-slate-500 mt-6" data-id="element-2714" data-mp="31cis65dr1d6z">
              {t('Aucune pièce jointe.', 'No attachment.')}
            </p> : <ul className="mt-5 space-y-2" data-id="element-2715" data-mp="2rlo3yvgy03iw">
              {attachments.map(file => <li key={file.name} className="group flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3" data-id="element-2716" data-mp="2dm6ai2spc237">
                  <PaperclipIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2717" data-mp="1yxwhmenvgc0j" />
                  <div className="min-w-0 flex-1" data-id="element-2718" data-mp="1tzbhsgcj7ucm">
                    <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2719" data-mp="1u0mux6rvaege">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2720" data-mp="27wrq5xee1y70">{file.size}</p>
                  </div>
                  <button onClick={() => setAttachments(prev => prev.filter(f => f.name !== file.name))} aria-label={t('Retirer la pièce jointe', 'Remove attachment')} className="p-2 rounded-lg text-slate-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-600 hover:bg-red-50 transition-all" data-id="element-2721" data-mp="23zvmxu369j04">
                    <TrashIcon className="w-4 h-4" data-id="element-2722" data-mp="30lp6vw2ybmxn" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {/* -------------------------------------------------------------- FEES */}
      {tab === 'frais' && <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2723" data-mp="1p03gx7eqgii6">
          {booking.billing.total === 0 ? <div className="py-14 text-center" data-id="element-2724" data-mp="22rrulf4mrf19">
              <div className="mx-auto w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-3" data-id="element-2725" data-mp="1xy5n01dbq1cb">
                <CheckIcon className="w-6 h-6 text-emerald-600" data-id="element-2726" data-mp="2cmjoa7v3dhqr" />
              </div>
              <p className="text-sm font-semibold text-slate-700" data-id="element-2727" data-mp="27nyqsg785c60">
                {t('Location sans frais', 'No-fee rental')}
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto" data-id="element-2728" data-mp="2lmgi0nwc96so">
                {t('Aucun montant n’est facturé pour cette réservation. Un contrat à 0 $ est tout de même produit.', 'No amount is billed for this booking. A $0 contract is still issued.')}
              </p>
            </div> : <>
              <table className="w-full text-sm text-left" data-id="element-2729" data-mp="d7ckmeedc2g7">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-2730" data-mp="h5s4waj2ewpx">
                  <tr data-id="element-2731" data-mp="3fyg1s7k8cg7">
                    <th className="px-6 py-3" data-id="element-2732" data-mp="3enapnyguwmz">{t('Description', 'Description')}</th>
                    <th className="px-6 py-3" data-id="element-2733" data-mp="8dq58egtlkgp">{t('Quantité', 'Quantity')}</th>
                    <th className="px-6 py-3" data-id="element-2734" data-mp="3pmwmuijdd3ux">{t('Taux', 'Rate')}</th>
                    <th className="px-6 py-3 text-right" data-id="element-2735" data-mp="3porba6tune3r">{t('Montant', 'Amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-2736" data-mp="3gm8bjvld1hlq">
                  <tr data-id="element-2737" data-mp="3go1ilbzvy3u9">
                    <td className="px-6 py-3.5 text-slate-800" data-id="element-2738" data-mp="3ll1krbtm6mg3">
                      {resourcePath(booking.resourceId, lang)} —{' '}
                      {usage?.label[lang]}
                    </td>
                    <td className="px-6 py-3.5 text-slate-600 tabular-nums" data-id="element-2739" data-mp="1jtetd95t8pj3">
                      {slots.length} {t('plages', 'slots')}
                    </td>
                    <td className="px-6 py-3.5 text-slate-600 tabular-nums" data-id="element-2740" data-mp="1hgcq2batdwd6">
                      {money(Math.round(booking.billing.total / slots.length))}
                    </td>
                    <td className="px-6 py-3.5 text-right font-medium text-slate-900 tabular-nums" data-id="element-2741" data-mp="18f4rrt8asvj3">
                      {money(booking.billing.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="border-t border-slate-100 px-6 py-4 space-y-2" data-id="element-2742" data-mp="1w3xpnv8hddgc">
                {[{
            label: t('Facturé à ce jour', 'Invoiced to date'),
            value: booking.billing.invoiced
          }, {
            label: t('Encaissé', 'Received'),
            value: booking.billing.paid
          }].map(row => <div key={row.label} className="flex justify-between text-sm" data-id="element-2743" data-mp="2ju0hqbceajey">
                    <span className="text-slate-500" data-id="element-2744" data-mp="2f31xj0oad16f">{row.label}</span>
                    <span className="font-medium text-slate-800 tabular-nums" data-id="element-2745" data-mp="2f0p51tkwj8f4">
                      {money(row.value)}
                    </span>
                  </div>)}
                <div className="flex justify-between text-sm pt-2 border-t border-slate-100" data-id="element-2746" data-mp="2rd9py1960c2f">
                  <span className="font-semibold text-slate-700" data-id="element-2747" data-mp="2wajdtcgwofo5">
                    {t('Solde à payer', 'Balance due')}
                  </span>
                  <span className={`font-bold tabular-nums ${booking.billing.balance > 0 ? 'text-amber-700' : 'text-emerald-600'}`} data-id="element-2748" data-mp="35d2afeu3lw1y">
                    {money(booking.billing.balance)}
                  </span>
                </div>
              </div>
            </>}
        </section>}
    </div>;
}